/**
 * La lecture de la page d'une offre ouverte par son lien — celle de `GroupAttributionProposalScreen`
 * et de `SubstitutionProposalScreen` :
 *  - par `hash` (le lien d'un mail), l'offre seule (`…ByHash`), tenue ici (patron B) ;
 *  - par id (la notification), la liste qui la porte, rangée dans le compte (patron A) : la liste des
 *    groupes (`getGroupAttributionProposals`, sans paramètre, comme l'ancien écran) ou le compte, qui
 *    porte les remplacements à venir (`getUser`). L'attente dure jusqu'à la réponse ; une offre
 *    absente ensuite est « introuvable ».
 * Une lecture par `hash` qui échoue (lien périmé) rend l'offre introuvable, comme l'ancien écran.
 */
import { useCallback, useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { authService } from "../../../../../../data/services/authService"
import { groupAttributionProposalsService } from "../../../../../../data/services/groupAttributionProposalsService"
import { substitutionProposalsService } from "../../../../../../data/services/substitutionProposalsService"
import { type TutorOfferLink } from "../../../../view-models/tutor/Availability/tutorOfferViewModel"
import { useApiFetch } from "../../../hooks/useApiFetch"

const BY_HASH = {
    group: groupAttributionProposalsService.getGroupAttributionProposalByHash,
    substitution: substitutionProposalsService.getSubstitutionProposalByHash,
}

const LIST = {
    group: groupAttributionProposalsService.getGroupAttributionProposals,
    substitution: authService.getUser,
}

export function useTutorOfferData(link: TutorOfferLink) {
    const dispatch = useDispatch()
    const byHash = useApiFetch<unknown>({
        service: BY_HASH[link.kind],
        urlParams: { hash: link.hash },
        requestKey: link.hash,
        enabled: link.hash != null,
    })

    const listService = LIST[link.kind]
    const byId = link.hash == null && link.id != null
    // La liste est lue par d'autres écrans : l'attente se clôt sur la réponse de NOTRE lecture, succès
    // ou échec, pas sur la tranche qu'ils partagent.
    const [listSettled, setListSettled] = useState(false)
    const reloadList = useCallback(() => {
        if (!byId) return
        dispatch(
            apiRequest(listService, {}, {}, {}, null, null, {
                offlineDialog: true,
                onResult: () => setListSettled(true),
            }) as never
        )
    }, [dispatch, listService, byId])
    useEffect(() => {
        reloadList()
    }, [reloadList, link.id])

    return {
        raw: byHash.raw,
        loading: link.hash != null ? byHash.loading : byId && !listSettled,
        reloadList,
    }
}
